import { createStart, createMiddleware } from "@tanstack/react-start";

import { renderErrorPage } from "./lib/error-page";
import { readStoredAuthSession } from "./lib/auth-session";
import { supabase } from "./integrations/supabase/client";

// DO NOT import attachSupabaseAuth — Web Locks deadlock. Use attachStoredAuth.

// NOTE: We intentionally do NOT import `attachSupabaseAuth` here. Its
// `supabase.auth.getSession()` call can hang on mobile Safari and stall
// journal/dream/shadow-work save + load requests. `attachStoredAuth`
// below reads the token synchronously from localStorage (mobile-safe)
// and only falls back to `getSession()` behind a 1.5s timeout.

// Merged duplicate articles. The content was folded into the keeper on the
// right, so the old URL permanently redirects and its ranking signals
// consolidate onto one page instead of two competing ones.
const MERGED_BLOG_SLUGS: Record<string, string> = {
  "4-trauma-responses-fight-flight-freeze-fawn": "trauma-responses",
  "emotional-flashbacks-what-they-are-and-how-to-cope": "emotional-flashbacks-cptsd",
  "100-signs-of-childhood-trauma-in-adults": "signs-of-childhood-trauma-in-adults",
};

// Retired URLs with a direct replacement — 301 so inbound links and any
// ranking signals land on the live page instead of a 404.
const MOVED_PATHS: Record<string, string> = {
  "/learn/breathing": "/breathing",
  "/learn/grounding": "/grounding",
};

// Single preferred host: the apex domain. www.recoverytrauma.com is 301'd so
// search engines only ever index one version of every URL.
const canonicalHostMiddleware = createMiddleware().server(async ({ next, request }) => {
  const url = new URL(request.url);
  if (url.hostname === "www.recoverytrauma.com") {
    url.hostname = "recoverytrauma.com";
    return new Response(null, { status: 301, headers: { location: url.toString() } });
  }

  // Retired paths that still receive inbound links. Each one points at the
  // page that replaced it so the 404 becomes a permanent redirect.
  const movedPath = MOVED_PATHS[url.pathname.replace(/\/$/, "") || "/"];
  if (movedPath) {
    url.pathname = movedPath;
    return new Response(null, { status: 301, headers: { location: url.toString() } });
  }

  const blogMatch = /^\/blog\/([^/]+)\/?$/.exec(url.pathname);
  const mergedInto = blogMatch ? MERGED_BLOG_SLUGS[decodeURIComponent(blogMatch[1])] : undefined;
  if (mergedInto) {
    url.pathname = `/blog/${mergedInto}`;
    return new Response(null, { status: 301, headers: { location: url.toString() } });
  }

  return next();
});

const errorMiddleware = createMiddleware().server(async ({ next, request }) => {
  const url = new URL(request.url);
  if (url.pathname.startsWith("/lovable/")) {
    return next();
  }
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

// Read ANY stored token (even if slightly expired). Safe to forward because
// the server always re-validates the JWT — but this lets a request go through
// while a background refresh is still finishing on mobile Safari.
function readAnyStoredToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const keys = Object.keys(window.localStorage).filter(
      (k) => k.startsWith("sb-") && k.endsWith("-auth-token"),
    );
    for (const k of keys) {
      const raw = window.localStorage.getItem(k);
      if (!raw) continue;
      try {
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        const s =
          (parsed.currentSession as { access_token?: string } | undefined) ??
          (parsed.session as { access_token?: string } | undefined) ??
          (parsed as { access_token?: string });
        if (s?.access_token) return s.access_token;
      } catch {
        // ignore malformed values
      }
    }
  } catch {
    // localStorage can throw in some private/embedded contexts
  }
  return null;
}

async function resolveAccessToken(): Promise<string | null> {
  // 1. Prefer a non-expired stored session (synchronous, mobile-safe).
  const fresh = readStoredAuthSession()?.access_token ?? null;
  if (fresh) return fresh;

  // 2. Ask Supabase for a session (may trigger a refresh), bounded so it
  //    can never hang mobile Safari. Fall back to any stored token.
  let refreshed: string | null = null;
  try {
    refreshed = await Promise.race<string | null>([
      supabase.auth
        .getSession()
        .then(({ data }) => data.session?.access_token ?? null)
        .catch(() => null),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), 1500)),
    ]);
  } catch {
    // ignore
  }

  return refreshed ?? readAnyStoredToken();
}

const attachStoredAuth = createMiddleware({ type: "function" }).client(
  async ({ next }) => {
    const token = await resolveAccessToken();
    return next({
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  },
);

export const startInstance = createStart(() => ({
  requestMiddleware: [canonicalHostMiddleware, errorMiddleware],
  functionMiddleware: [attachStoredAuth],
}));
